import { useMutation, useQueryClient, QueryClient } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import {
  CreateDirectDebitDto,
  CreateExportBatchDto,
  CreateMandateDto,
  CreateSepaRefundDto,
  DocumentDto,
  RevokeMandateDto,
  UpdateMandateDto,
  UploadSepaMandateSignedDocumentDocuTypeEnum,
  UploadSepaMandateSignedDocumentOwnerTypeEnum,
} from "../../api-client";
import { sepaKeys } from "./sepa.keys";
import { transactionKeys } from "../Transactions/transaction.keys";

/**
 * SEPA write hooks (react-query).
 *
 * Success/error notifications are handled globally by the MutationCache in
 * `queryClient.ts` (via `meta.successMessage` / `meta.errorMessage`), so these
 * mutations only declare the message and do cache invalidation — no manual
 * `notificationStore` calls.
 */

/**
 * Invalidations shared by any SEPA transaction that affects a patient's billing
 * (direct debit / refund). Keeps the Rechnungen tab, timeline and due amounts in
 * sync with the new payment.
 */
function invalidateBillingForTransaction(
  qc: QueryClient,
  patientId?: number,
  rxId?: string,
): Promise<unknown>[] {
  const invalidations: Promise<unknown>[] = [
    qc.invalidateQueries({ queryKey: sepaKeys.transactions.all }),
  ];
  if (patientId) {
    invalidations.push(
      qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.forPatient(patientId) }),
      qc.invalidateQueries({ queryKey: transactionKeys.patientBillsInfo.forPatient(patientId) }),
      qc.invalidateQueries({ queryKey: transactionKeys.patientTransactions.forPatient(patientId) }),
      qc.invalidateQueries({ queryKey: transactionKeys.unpaidBadgeCount.forPatient(patientId) }),
    );
  }
  if (rxId) {
    invalidations.push(
      qc.invalidateQueries({ queryKey: transactionKeys.rxBillingTimeline.forRx(rxId) }),
      qc.invalidateQueries({ queryKey: transactionKeys.rxDuePayment.forRx(rxId) }),
      qc.invalidateQueries({ queryKey: transactionKeys.duePaymentByRx.forRx(rxId) }),
    );
  }
  return invalidations;
}

// ─────────────────────────────── Mandates ───────────────────────────────

/** Creates a SEPA direct-debit mandate (reuses/creates a bank account). */
export const useCreateSepaMandate = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateMandateDto) => sepa.createSepaMandate(dto),
    meta: { successMessage: "SEPA-Mandat erstellt." },
    onSuccess: (_data, dto) =>
      qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.forPatient(dto.patientId) }),
  });
};

/** Corrects a DRAFT mandate's bank details / cap / validity (active mandates are locked). */
export const useUpdateSepaMandate = (patientId: number) => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateMandateDto }) =>
      sepa.updateSepaMandate(id, dto),
    meta: { successMessage: "Mandat aktualisiert." },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.forPatient(patientId) }),
  });
};

/** Revokes a mandate with a reason (kept for audit); cancels its unsent collections. */
export const useRevokeSepaMandate = (patientId?: number) => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: RevokeMandateDto }) =>
      sepa.revokeSepaMandate(id, dto),
    meta: { successMessage: "Mandat widerrufen." },
    // Revoking cancels the mandate's unsent collections, so the billing views
    // (bills table, transactions, due amounts) must be refreshed too — not just
    // the bank-accounts card.
    onSuccess: () =>
      Promise.all([
        ...invalidateBillingForTransaction(qc, patientId),
        patientId
          ? qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.forPatient(patientId) })
          : qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.all }),
      ]),
  });
};

/** Soft-deletes a mandate. */
export const useDeleteSepaMandate = (patientId?: number) => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sepa.deleteSepaMandate(id),
    meta: { successMessage: "Mandat gelöscht." },
    onSuccess: () =>
      patientId
        ? qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.forPatient(patientId) })
        : qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.all }),
  });
};

/** Soft-deletes a bank account. */
export const useDeleteSepaBankAccount = (patientId?: number) => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sepa.deleteSepaBankAccount(id),
    meta: { successMessage: "Bankverbindung gelöscht." },
    onSuccess: () =>
      patientId
        ? qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.forPatient(patientId) })
        : qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.all }),
  });
};

/**
 * Generates (and caches) the fillable SEPA mandate form PDF for signing and
 * returns the document with a temporary access URL. The caller opens the URL.
 */
export const useGenerateSepaMandateForm = () => {
  const { sepa } = useApi();

  return useMutation({
    mutationFn: (id: string) => sepa.generateSepaMandateFormPdf(id) as Promise<DocumentDto>,
    meta: { silenceSuccess: true },
  });
};

/**
 * Uploads the signed mandate document and activates the mandate. Activation is a
 * deliberate step: the caller must pass the ACTUAL signature date and an explicit
 * confirmation that the document is readable, signed and matches the entered data.
 */
export const useUploadSepaMandateSignedDocument = (patientId: number) => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      file,
      signatureDate,
      confirmed,
    }: {
      id: string;
      file: File;
      signatureDate: string;
      confirmed: boolean;
    }) =>
      sepa.uploadSepaMandateSignedDocument(
        id,
        UploadSepaMandateSignedDocumentDocuTypeEnum.Mandat,
        UploadSepaMandateSignedDocumentOwnerTypeEnum.Patient,
        String(patientId),
        file,
        signatureDate,
        confirmed ? "true" : "false",
        true,
      ),
    meta: { successMessage: "Signiertes Mandat hochgeladen & best\u00e4tigt." },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: sepaKeys.bankAccounts.forPatient(patientId) }),
  });
};

// ───────────────────────────── Transactions ─────────────────────────────

/** Creates a direct-debit transaction against an existing ACTIVE mandate. */
export const useCreateSepaDirectDebit = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateDirectDebitDto) => sepa.createSepaDirectDebit(dto),
    meta: { successMessage: "Lastschrift erstellt." },
    onSuccess: (_data, dto) =>
      Promise.all(invalidateBillingForTransaction(qc, dto.patientId, dto.rxId)),
  });
};

/** Creates a refund (credit transfer) to the patient. */
export const useCreateSepaRefund = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateSepaRefundDto) => sepa.createSepaRefund(dto),
    meta: { successMessage: "Rückerstattung erstellt." },
    onSuccess: (_data, dto) =>
      Promise.all(invalidateBillingForTransaction(qc, dto.patientId, dto.rxId)),
  });
};

/** Cancels a still-PENDING transaction. */
export const useCancelSepaTransaction = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sepa.cancelSepaTransaction(id),
    meta: { successMessage: "Transaktion storniert." },
    onSuccess: () => qc.invalidateQueries({ queryKey: sepaKeys.transactions.all }),
  });
};

/**
 * Reverses (storniert) a SEPA transaction: keeps the original visible as REVERSED
 * and books a reversing entry. Invalidates the transaction lists and the patient's
 * billing so Rx due amounts recompute.
 */
export const useReverseSepaTransaction = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      sepa.reverseSepaTransaction(id, { reason }),
    meta: { successMessage: "Transaktion storniert." },
    // The reversal response carries the original's patient/rx, so we refresh the
    // patient bills, Rx due amount and billing timeline without a full reload.
    onSuccess: (data) =>
      Promise.all(invalidateBillingForTransaction(qc, data?.patient?.id, data?.rx?.id)),
  });
};

// ─────────────────────────────── Export batches ───────────────────────────

/** Exports PENDING transactions for an account + direction into one SEPA file. */
export const useCreateSepaExportBatch = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateExportBatchDto) => sepa.createSepaExportBatch(dto),
    meta: { successMessage: "Export erstellt." },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: sepaKeys.transactions.all }),
        qc.invalidateQueries({ queryKey: sepaKeys.exportBatches.all }),
      ]),
  });
};

/** Rolls back a generated (not yet sent) export with a mandatory reason; its transactions return to PENDING. */
export const useUndoSepaExportBatch = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      sepa.undoSepaExportBatch(id, { reason }),
    meta: { successMessage: "Export rückgängig gemacht." },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: sepaKeys.transactions.all }),
        qc.invalidateQueries({ queryKey: sepaKeys.exportBatches.all }),
      ]),
  });
};

/** Marks a generated export as submitted to the bank (locks it against rollback). */
export const useMarkSepaExportSent = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sepa.markSepaExportSent(id),
    meta: { successMessage: "Als an die Bank gesendet markiert." },
    onSuccess: () => qc.invalidateQueries({ queryKey: sepaKeys.exportBatches.all }),
  });
};

/** Records a bank acknowledgement; the export's transactions become SETTLED. */
export const useMarkSepaExportAccepted = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sepa.markSepaExportAccepted(id),
    meta: { successMessage: "Bankbestätigung erfasst." },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: sepaKeys.transactions.all }),
        qc.invalidateQueries({ queryKey: sepaKeys.exportBatches.all }),
      ]),
  });
};

/** Records a bank rejection with a reason; the export's transactions become RETURNED. */
export const useMarkSepaExportRejected = () => {
  const { sepa } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      sepa.markSepaExportRejected(id, { reason }),
    meta: { successMessage: "Bankablehnung erfasst." },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: sepaKeys.transactions.all }),
        qc.invalidateQueries({ queryKey: sepaKeys.exportBatches.all }),
      ]),
  });
};
